/**
 * Whether a surface should still wear its BETA pill (SE-8200).
 *
 * ## A kill-switch, not a rollout gate
 *
 * The badge shows unless someone has created the flag and switched it OFF.
 * That direction is the point of the ticket: the pill must be visible now,
 * with nothing configured in PostHog, and must come off when the feature
 * stabilises WITHOUT a deploy. A conventional rollout gate would invert both
 * halves — nothing visible until someone remembers to create the flag.
 *
 * ## Why this reads the flag MAP rather than the flag
 *
 * The obvious spelling is `usePostHogFeatureFlag(key) !== false`. It was the
 * first implementation, and it is wrong: PostHog reports a flag it has never
 * heard of as a real boolean `false`, not `undefined`. `undefined` means only
 * "still loading". So `!== false` hid the badge in exactly the state the
 * feature ships in — no flag configured — which is the failure the design was
 * supposed to rule out.
 *
 * That was measured on the dev project rather than reasoned about: with
 * `compliance-tab-beta-dev` uncreated, `getFeatureFlag` returned
 * `boolean:false` while `getFeatureFlags()` held 25 keys and none of them was
 * that one. Presence in the map is therefore the only evidence available that
 * a flag exists, and this hook is built on it: ABSENT means nobody has
 * decided, and nobody deciding means the pill stays.
 *
 * A note on the `isNavItemVisible` precedent, which this originally cited: it
 * has the same `=== false` shape and is correct there only because
 * `squad-role-gating` is a flag that EXISTS. The shape does not transfer to a
 * flag that may not.
 *
 * While the map is still loading it is `undefined`, and the badge shows — a
 * loading flag is not a decision to hide, and the pill flickering off then on
 * would be worse than showing it a beat early.
 *
 * ## Why the env suffix is not optional
 *
 * dev, staging and production share one PostHog project (SE-8003), so a bare
 * key would tie all three environments to one switch — taking Compliance out
 * of beta on staging would silently do the same on production.
 * @module hooks/useBetaBadgeVisible
 */
import { withEnvSuffix } from "@/config/featureFlags";
import usePostHogFeatureFlagMap from "@/hooks/usePostHogFeatureFlagMap";

/**
 * Reads a beta-badge kill-switch.
 * @param flagKey - The bare flag key, e.g. `POSTHOG_FLAGS.MATCHES_TAB_BETA`.
 * @returns `false` only when the flag exists AND is off; `true` otherwise.
 */
const useBetaBadgeVisible = (flagKey: string): boolean => {
  const flags = usePostHogFeatureFlagMap();
  if (flags === undefined) return true;
  const key = withEnvSuffix(flagKey);
  if (!Object.hasOwn(flags, key)) return true;
  return flags[key] !== false;
};

export default useBetaBadgeVisible;
